import { apiClient } from './api-client';
import { Customer, CustomerApiKey, CustomerSubscription, CustomerTeam } from '@/mocks/data/customers/types';
import { PaginatedResponse } from '@/lib/data-source/types';

export interface CustomerListParams {
  search?: string;
  status?: 'active' | 'trial' | 'inactive' | 'suspended' | 'all';
  role?: 'owners' | 'members' | 'individual' | 'all';
  page?: number;
  per_page?: number;
}

export interface CustomerCreateData {
  firstName: string;
  lastName: string;
  email: string;
  language?: string;
  phone?: string;
  company?: string;
}

export interface CustomerUpdateData extends Partial<CustomerCreateData> {
  status?: 'active' | 'trial' | 'inactive' | 'suspended';
}

export interface ApiKeyCreateData {
  name: string;
  permissions: string[];
  type?: 'personal' | 'team';
}

export interface SubscriptionCreateData {
  product_id: string;
  product_name: string;
  plan_name: string;
  monthly_cost: number;
  usage_limit: number;
}

export interface TeamCreateData {
  name: string;
  total_seats: number;
  role?: 'owner' | 'member';
}

class CustomersService {
  // Customer CRUD operations
  async getCustomers(params?: CustomerListParams): Promise<PaginatedResponse<Customer>> {
    const response = await apiClient.get<PaginatedResponse<Customer>>('/api/v1/customers', { params });
    return response.data;
  }

  async getCustomer(id: string): Promise<Customer> {
    const response = await apiClient.get<{ data: Customer }>(`/api/v1/customers/${id}`);
    return response.data.data;
  }

  async createCustomer(data: CustomerCreateData): Promise<Customer> {
    const response = await apiClient.post<{ data: Customer }>('/api/v1/customers', data);
    return response.data.data;
  }

  async updateCustomer(id: string, data: CustomerUpdateData): Promise<Customer> {
    const response = await apiClient.put<{ data: Customer }>(`/api/v1/customers/${id}`, data);
    return response.data.data;
  }

  async deleteCustomer(id: string): Promise<void> {
    await apiClient.delete(`/api/v1/customers/${id}`);
  }

  // API Key operations
  async getCustomerApiKeys(customerId: string): Promise<CustomerApiKey[]> {
    const response = await apiClient.get<{ data: CustomerApiKey[] }>(`/api/v1/customers/${customerId}/api-keys`);
    return response.data.data;
  }

  async createCustomerApiKey(customerId: string, data: ApiKeyCreateData): Promise<CustomerApiKey> {
    const response = await apiClient.post<{ data: CustomerApiKey }>(`/api/v1/customers/${customerId}/api-keys`, data);
    return response.data.data;
  }

  async updateCustomerApiKey(customerId: string, keyId: string, data: Partial<ApiKeyCreateData>): Promise<CustomerApiKey> {
    const response = await apiClient.put<{ data: CustomerApiKey }>(`/api/v1/customers/${customerId}/api-keys/${keyId}`, data);
    return response.data.data;
  }

  async deleteCustomerApiKey(customerId: string, keyId: string): Promise<void> {
    await apiClient.delete(`/api/v1/customers/${customerId}/api-keys/${keyId}`);
  }

  async regenerateCustomerApiKey(customerId: string, keyId: string): Promise<CustomerApiKey> {
    const response = await apiClient.post<{ data: CustomerApiKey }>(`/api/v1/customers/${customerId}/api-keys/${keyId}/regenerate`);
    return response.data.data;
  }

  // Subscription operations
  async getCustomerSubscriptions(customerId: string): Promise<CustomerSubscription[]> {
    const response = await apiClient.get<{ data: CustomerSubscription[] }>(`/api/v1/customers/${customerId}/subscriptions`);
    return response.data.data;
  }

  async getCustomerSubscription(customerId: string, subscriptionId: string): Promise<CustomerSubscription> {
    const response = await apiClient.get<{ data: CustomerSubscription }>(`/api/v1/customers/${customerId}/subscriptions/${subscriptionId}`);
    return response.data.data;
  }

  async createCustomerSubscription(customerId: string, data: SubscriptionCreateData): Promise<CustomerSubscription> {
    const response = await apiClient.post<{ data: CustomerSubscription }>(`/api/v1/customers/${customerId}/subscriptions`, data);
    return response.data.data;
  }

  async updateCustomerSubscription(customerId: string, subscriptionId: string, data: Partial<SubscriptionCreateData>): Promise<CustomerSubscription> {
    const response = await apiClient.put<{ data: CustomerSubscription }>(`/api/v1/customers/${customerId}/subscriptions/${subscriptionId}`, data);
    return response.data.data;
  }

  async cancelCustomerSubscription(customerId: string, subscriptionId: string): Promise<CustomerSubscription> {
    const response = await apiClient.post<{ data: CustomerSubscription }>(`/api/v1/customers/${customerId}/subscriptions/${subscriptionId}/cancel`);
    return response.data.data;
  }

  async reactivateCustomerSubscription(customerId: string, subscriptionId: string): Promise<CustomerSubscription> {
    const response = await apiClient.post<{ data: CustomerSubscription }>(`/api/v1/customers/${customerId}/subscriptions/${subscriptionId}/reactivate`);
    return response.data.data;
  }

  async deleteCustomerSubscription(customerId: string, subscriptionId: string): Promise<void> {
    await apiClient.delete(`/api/v1/customers/${customerId}/subscriptions/${subscriptionId}`);
  }

  // Team operations
  async getCustomerTeams(customerId: string): Promise<CustomerTeam[]> {
    const response = await apiClient.get<{ data: CustomerTeam[] }>(`/api/v1/customers/${customerId}/teams`);
    return response.data.data;
  }

  async getCustomerTeam(customerId: string, teamId: string): Promise<CustomerTeam> {
    const response = await apiClient.get<{ data: CustomerTeam }>(`/api/v1/customers/${customerId}/teams/${teamId}`);
    return response.data.data;
  }

  async createCustomerTeam(customerId: string, data: TeamCreateData): Promise<CustomerTeam> {
    const response = await apiClient.post<{ data: CustomerTeam }>(`/api/v1/customers/${customerId}/teams`, data);
    return response.data.data;
  }

  async updateCustomerTeam(customerId: string, teamId: string, data: Partial<TeamCreateData>): Promise<CustomerTeam> {
    const response = await apiClient.put<{ data: CustomerTeam }>(`/api/v1/customers/${customerId}/teams/${teamId}`, data);
    return response.data.data;
  }

  async leaveCustomerTeam(customerId: string, teamId: string): Promise<void> {
    await apiClient.post(`/api/v1/customers/${customerId}/teams/${teamId}/leave`);
  }

  async deleteCustomerTeam(customerId: string, teamId: string): Promise<void> {
    await apiClient.delete(`/api/v1/customers/${customerId}/teams/${teamId}`);
  }

  async addTeamMember(customerId: string, teamId: string, email: string): Promise<void> {
    await apiClient.post(`/api/v1/customers/${customerId}/teams/${teamId}/members`, { email });
  }

  async removeTeamMember(customerId: string, teamId: string, memberId: string): Promise<void> {
    await apiClient.delete(`/api/v1/customers/${customerId}/teams/${teamId}/members/${memberId}`);
  }
}

export const customersService = new CustomersService();
